import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtDate, fmtMoney, formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Plus, ArrowsClockwise, PencilSimple, Trash, Receipt, ArrowClockwise, XCircle, Eye,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const FREQ_LABEL = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  half_yearly: "Half-Yearly",
  yearly: "Yearly",
};
const STATUS_STYLES = {
  active:   "bg-emerald-50 text-emerald-800 border-emerald-300",
  expired:  "bg-slate-100 text-slate-500 border-slate-300",
  cancelled:"bg-red-50 text-red-700 border-red-300",
  draft:    "bg-amber-50 text-amber-800 border-amber-300",
};

function StatusPill({ status }) {
  return (
    <span className={`inline-flex items-center rounded-sm border px-1.5 py-[1px] text-[10px] font-semibold uppercase tracking-widest ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>
      {status}
    </span>
  );
}

function KPI({ label, value, tone = "slate", testid }) {
  const cls = {
    slate: "bg-white border-slate-200 text-slate-900",
    blue: "bg-[#2E75B6] text-white border-[#2E75B6]",
    orange: "bg-[#EA580C] text-white border-[#EA580C]",
    dark: "bg-[#0F172A] text-white border-[#0F172A]",
  }[tone];
  return (
    <div className={`border ${cls} rounded-sm p-4`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-[0.14em] font-semibold ${tone === "slate" ? "text-slate-500" : "text-white/70"}`}>{label}</div>
      <div className="font-display text-[22px] font-bold font-mono-num mt-1">{value}</div>
    </div>
  );
}

export default function AMCContracts({ projectId, embedded = false }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManage = isAdmin || user?.role === "manager";

  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (projectId) params.set("project_id", projectId);
    if (statusFilter !== "all") params.set("status", statusFilter);
    const [a, b] = await Promise.all([
      api.get(`/amc-contracts?${params.toString()}`),
      embedded ? Promise.resolve({ data: null }) : api.get("/amc-contracts/stats"),
    ]);
    setRows(a.data || []);
    setStats(b.data);
  }, [projectId, statusFilter, embedded]);

  const loadProjects = useCallback(async () => {
    const { data } = await api.get("/projects");
    setProjects(data || []);
  }, []);

  useEffect(() => { load(); loadProjects(); }, [load, loadProjects]);

  const projectName = useMemo(() => {
    const m = Object.fromEntries(projects.map((p) => [p.id, p.name]));
    return (pid) => m[pid] || "—";
  }, [projects]);

  const del = async (c) => {
    if (!window.confirm(`Delete AMC contract ${c.contract_no}? All linked invoice references will remain but the contract will be removed.`)) return;
    try {
      await api.delete(`/amc-contracts/${c.id}`);
      toast.success(`${c.contract_no} deleted`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const doAction = async (verb, path, c) => {
    if (!window.confirm(`${verb} contract ${c.contract_no}?`)) return;
    try {
      await api.post(path);
      toast.success(`${c.contract_no} ${verb.toLowerCase()}d`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const genInvoice = async (c) => {
    try {
      const { data } = await api.post(`/amc-contracts/${c.id}/generate-invoice`);
      toast.success(`Invoice ${data.invoice.invoice_number} generated · ₹${data.invoice.total.toFixed(2)} (${data.instalment_number}/${data.total_instalments})`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="amc-page">
      {!embedded && (
        <PageHeader
          title="AMC / Service Contracts"
          module="Sales & Receivables · Recurring"
          subtitle="Annual Maintenance Contracts — tracks recurring revenue, generates instalment invoices, and reminds you before renewal."
          ribbon={
            <>
              <BcRibbonGroup label="New">
                {canManage && <BcRibbonBtn icon={Plus} label="New AMC" onClick={() => setCreating(true)} testid="amc-new-btn" />}
              </BcRibbonGroup>
              <BcRibbonGroup label="Process">
                <BcRibbonBtn icon={ArrowsClockwise} label="Refresh" onClick={load} testid="amc-refresh" />
              </BcRibbonGroup>
            </>
          }
        />
      )}

      <div className={embedded ? "space-y-4" : "p-6 space-y-5"}>
        {stats && !embedded && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label="Active Contracts" tone="dark" value={stats.active_contracts} testid="amc-kpi-active" />
            <KPI label="Annualised Value" tone="slate" value={fmtMoney(stats.annualised_value)} testid="amc-kpi-value" />
            <KPI label="Invoiced To Date" tone="blue" value={fmtMoney(stats.invoiced_to_date)} testid="amc-kpi-invoiced" />
            <KPI label="Remaining Billable" tone="orange" value={fmtMoney(stats.remaining_billable)} testid="amc-kpi-remaining" />
          </div>
        )}

        {!embedded && (
          <div className="flex items-center gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="amc-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
              </SelectContent>
            </Select>
            {embedded ? null : <div className="text-sm text-slate-500">{rows.length} contract(s)</div>}
          </div>
        )}

        {embedded && (
          <div className="flex items-center justify-between">
            <div className="label-caps">AMC Contracts ({rows.length})</div>
            {canManage && (
              <Button size="sm" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white h-8" onClick={() => setCreating(true)} data-testid="amc-embedded-new">
                <Plus size={14} className="mr-1" /> New AMC
              </Button>
            )}
          </div>
        )}

        {rows.length === 0 ? (
          <EmptyState title="No contracts yet" description="Raise your first AMC to start tracking recurring service revenue." />
        ) : (
          <div className="bc-card border border-slate-200 rounded-sm overflow-hidden" data-testid="amc-table">
            <Table className="bc-list">
              <TableHeader>
                <TableRow>
                  <TableHead>Contract #</TableHead>
                  <TableHead>Client</TableHead>
                  {!projectId && <TableHead>Project</TableHead>}
                  <TableHead>Period</TableHead>
                  <TableHead>Freq</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead className="text-right">Invoiced</TableHead>
                  <TableHead>Next Bill</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-40" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((c) => (
                  <TableRow key={c.id} data-testid={`amc-row-${c.id}`}>
                    <TableCell className="font-semibold text-slate-900 font-mono text-[11.5px]">{c.contract_no}</TableCell>
                    <TableCell>{c.client_name}</TableCell>
                    {!projectId && <TableCell className="text-slate-500 text-[12px]">{projectName(c.project_id)}</TableCell>}
                    <TableCell className="text-[12px] text-slate-600">{fmtDate(c.start_date)} → {fmtDate(c.end_date)}</TableCell>
                    <TableCell><span className="inline-flex items-center rounded-sm bg-slate-100 text-slate-700 px-1.5 py-[1px] text-[10px] font-semibold uppercase tracking-widest">{FREQ_LABEL[c.billing_frequency]}</span></TableCell>
                    <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(c.contract_value)}</TableCell>
                    <TableCell className="text-right font-mono-num text-slate-600">{fmtMoney(c.invoiced_amount)}</TableCell>
                    <TableCell className="text-[12px] text-slate-600 font-mono-num">{c.next_billing_date ? fmtDate(c.next_billing_date) : "—"}</TableCell>
                    <TableCell><StatusPill status={c.status} /></TableCell>
                    <TableCell>
                      <div className="flex gap-1 items-center">
                        <button onClick={() => setViewing(c)} title="View schedule" className="text-slate-500 hover:text-[#EA580C] p-1" data-testid={`amc-view-${c.id}`}><Eye size={14} /></button>
                        {canManage && c.status === "active" && (
                          <button onClick={() => genInvoice(c)} title="Generate next invoice" className="text-emerald-600 hover:text-emerald-800 p-1" data-testid={`amc-invoice-${c.id}`}><Receipt size={14} /></button>
                        )}
                        {canManage && c.status !== "cancelled" && (
                          <button onClick={() => doAction("Renew", `/amc-contracts/${c.id}/renew`, c)} title="Renew" className="text-blue-600 hover:text-blue-800 p-1" data-testid={`amc-renew-${c.id}`}><ArrowClockwise size={14} /></button>
                        )}
                        {canManage && (
                          <>
                            <button onClick={() => setEditing(c)} title="Edit" className="text-slate-500 hover:text-[#EA580C] p-1" data-testid={`amc-edit-${c.id}`}><PencilSimple size={14} /></button>
                            {c.status === "active" && (
                              <button onClick={() => doAction("Cancel", `/amc-contracts/${c.id}/cancel`, c)} title="Cancel" className="text-orange-600 hover:text-orange-800 p-1" data-testid={`amc-cancel-${c.id}`}><XCircle size={14} /></button>
                            )}
                          </>
                        )}
                        {isAdmin && (
                          <button onClick={() => del(c)} title="Delete" className="text-slate-400 hover:text-red-600 p-1" data-testid={`amc-delete-${c.id}`}><Trash size={14} /></button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {creating && (
        <AMCFormSheet
          open={creating}
          onOpenChange={setCreating}
          projects={projects}
          projectId={projectId}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
      {editing && (
        <AMCFormSheet
          open={!!editing}
          onOpenChange={(v) => !v && setEditing(null)}
          initial={editing}
          projects={projects}
          projectId={projectId}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {viewing && (
        <AMCDetailDialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)} contract={viewing} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create/Edit sheet
// ---------------------------------------------------------------------------
const EMPTY = {
  contract_no: "",
  client_name: "",
  client_gstin: "",
  project_id: "",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: (() => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); })(),
  contract_value: 0,
  billing_frequency: "quarterly",
  visits_per_year: 4,
  gst_rate: 18,
  equipment: [],
  scope_notes: "",
  auto_renew: false,
  auto_invoice: false,
};

function AMCFormSheet({ open, onOpenChange, initial, projects, projectId, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      const base = initial ? { ...EMPTY, ...initial } : { ...EMPTY, project_id: projectId || "" };
      setForm(base);
    }
  }, [open, initial, projectId]);

  const patchEquip = (i, k, v) => setForm((f) => ({ ...f, equipment: f.equipment.map((e, idx) => (idx === i ? { ...e, [k]: v } : e)) }));

  const save = async () => {
    if (!form.client_name?.trim()) return toast.error("Client name is required");
    if (Number(form.contract_value) <= 0) return toast.error("Contract value must be > 0");
    if (form.end_date <= form.start_date) return toast.error("End date must be after start");
    setSaving(true);
    try {
      const payload = {
        ...form,
        contract_value: Number(form.contract_value),
        visits_per_year: Number(form.visits_per_year || 0),
        gst_rate: Number(form.gst_rate || 0),
        project_id: form.project_id || null,
        equipment: (form.equipment || []).map((e) => ({ label: e.label, equipment_id: e.equipment_id || "", quantity: Number(e.quantity || 1) })).filter((e) => e.label),
      };
      if (initial?.id) await api.put(`/amc-contracts/${initial.id}`, payload);
      else await api.post("/amc-contracts", payload);
      toast.success(initial ? "Contract updated" : "Contract created");
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[600px] sm:max-w-xl lg:w-[min(720px,95vw)] lg:max-w-none overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display">{initial ? "Edit AMC Contract" : "New AMC Contract"}</SheetTitle>
        </SheetHeader>
        <div className="mt-5 space-y-4" data-testid="amc-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contract # (auto if blank)">
              <Input value={form.contract_no} onChange={(e) => setForm({ ...form, contract_no: e.target.value })} placeholder="AMC-2026-001" data-testid="amc-form-no" />
            </Field>
            <Field label="Status">
              <Input value={form.status || (initial ? initial.status : "active")} disabled className="bg-slate-50 text-slate-500" />
            </Field>
            <Field label="Client Name *">
              <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="amc-form-client" />
            </Field>
            <Field label="Client GSTIN">
              <Input value={form.client_gstin} onChange={(e) => setForm({ ...form, client_gstin: e.target.value })} className="font-mono uppercase" />
            </Field>
            {!projectId && (
              <Field label="Project (optional)" colSpan={2}>
                <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="amc-form-project"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Start Date"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="amc-form-start" /></Field>
            <Field label="End Date"><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="amc-form-end" /></Field>
            <Field label="Contract Value (₹) *"><Input type="number" value={form.contract_value} onChange={(e) => setForm({ ...form, contract_value: e.target.value })} data-testid="amc-form-value" /></Field>
            <Field label="Billing Frequency">
              <Select value={form.billing_frequency} onValueChange={(v) => setForm({ ...form, billing_frequency: v })}>
                <SelectTrigger data-testid="amc-form-freq"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="half_yearly">Half-Yearly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Visits / Year"><Input type="number" value={form.visits_per_year} onChange={(e) => setForm({ ...form, visits_per_year: e.target.value })} /></Field>
            <Field label="GST Rate %"><Input type="number" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: e.target.value })} /></Field>
          </div>

          {/* Equipment list */}
          <div className="border border-slate-200 rounded-sm">
            <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Equipment covered · {form.equipment?.length || 0}</div>
              <button onClick={() => setForm({ ...form, equipment: [...(form.equipment || []), { label: "", quantity: 1, equipment_id: "" }] })} className="text-[11px] font-semibold text-[#EA580C] hover:underline flex items-center gap-1" data-testid="amc-add-equip">
                <Plus size={12} /> Add row
              </button>
            </div>
            <div className="p-3 space-y-2">
              {(form.equipment || []).length === 0 && <div className="text-xs text-slate-400 py-2">No equipment listed yet.</div>}
              {(form.equipment || []).map((e, i) => (
                <div key={`equip-${i}-${e.label}`} className="grid grid-cols-[1fr_100px_28px] gap-2 items-center">
                  <Input placeholder="e.g. 3-ton VRF outdoor unit — Roof, Block A" value={e.label} onChange={(ev) => patchEquip(i, "label", ev.target.value)} className="h-8" data-testid={`amc-equip-label-${i}`} />
                  <Input type="number" placeholder="Qty" value={e.quantity} onChange={(ev) => patchEquip(i, "quantity", ev.target.value)} className="h-8 font-mono-num text-right" />
                  <button onClick={() => setForm({ ...form, equipment: form.equipment.filter((_, idx) => idx !== i) })} className="text-slate-400 hover:text-red-600"><Trash size={12} /></button>
                </div>
              ))}
            </div>
          </div>

          <Field label="Scope Notes">
            <Textarea rows={3} value={form.scope_notes} onChange={(e) => setForm({ ...form, scope_notes: e.target.value })} placeholder="What's included / excluded, SLA, emergency callout window …" data-testid="amc-form-scope" />
          </Field>

          <div className="flex items-center gap-4 text-[13px]">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.auto_renew} onChange={(e) => setForm({ ...form, auto_renew: e.target.checked })} />
              Auto-renew at term end
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.auto_invoice} onChange={(e) => setForm({ ...form, auto_invoice: e.target.checked })} />
              Auto-invoice on billing date
            </label>
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="amc-form-save" className="flex-1 rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">{saving ? "Saving…" : (initial ? "Save changes" : "Create Contract")}</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Detail (billing schedule + linked invoices)
// ---------------------------------------------------------------------------
function AMCDetailDialog({ open, onOpenChange, contract }) {
  const [full, setFull] = useState(null);
  useEffect(() => {
    if (open && contract?.id) {
      api.get(`/amc-contracts/${contract.id}`).then((r) => setFull(r.data));
    }
  }, [open, contract]);
  if (!open) return null;
  const c = full || contract;
  const schedule = c.billing_schedule || [];
  const invoicedCount = (c.invoice_ids || []).length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="amc-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            <span>{c.contract_no}</span>
            <StatusPill status={c.status} />
          </DialogTitle>
          <DialogDescription className="sr-only">
            AMC contract billing schedule and equipment coverage details.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-3 gap-3 text-[13px]">
            <div><div className="label-caps text-slate-500">Client</div><div className="font-semibold">{c.client_name}</div><div className="text-slate-500 font-mono text-[11px]">{c.client_gstin}</div></div>
            <div><div className="label-caps text-slate-500">Period</div><div className="font-mono-num">{fmtDate(c.start_date)} → {fmtDate(c.end_date)}</div></div>
            <div><div className="label-caps text-slate-500">Value</div><div className="font-mono-num font-semibold text-[#EA580C]">{fmtMoney(c.contract_value)}</div><div className="text-[11px] text-slate-500">{FREQ_LABEL[c.billing_frequency]}</div></div>
          </div>
          {c.scope_notes && (
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-[13px] text-slate-700 whitespace-pre-wrap">{c.scope_notes}</div>
          )}
          {c.equipment && c.equipment.length > 0 && (
            <div>
              <div className="label-caps mb-2">Equipment covered</div>
              <ul className="text-[13px] list-disc pl-5 space-y-1">
                {c.equipment.map((e, i) => <li key={`eq-${i}-${e.label}`}>{e.label} <span className="text-slate-400">× {e.quantity}</span></li>)}
              </ul>
            </div>
          )}
          <div>
            <div className="label-caps mb-2">Billing schedule · {invoicedCount}/{schedule.length} invoiced</div>
            <Table className="bc-list">
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Billing Date</TableHead>
                  <TableHead className="text-right">Amount (base)</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedule.map((s, i) => (
                  <TableRow key={`sched-${s.date}-${i}`} data-testid={`amc-schedule-row-${i}`}>
                    <TableCell className="font-mono-num">{i + 1}</TableCell>
                    <TableCell className="font-mono-num">{fmtDate(s.date)}</TableCell>
                    <TableCell className="text-right font-mono-num">{fmtMoney(s.amount)}</TableCell>
                    <TableCell>
                      {i < invoicedCount ? (
                        <span className="text-emerald-700 text-[11px] font-semibold uppercase tracking-widest">Invoiced</span>
                      ) : (
                        <span className="text-slate-400 text-[11px] font-semibold uppercase tracking-widest">Pending</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, colSpan }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : "col-span-1"}>
      <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
